<template>
  <main-template>
    <v-row class="mb-2">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ modoEdicao ? "Editar Justificativa em Massa" : "Justificar em Massa" }}
        </h1>
        <p class="body-2 ml-8 mb-0">
          {{
            modoEdicao
              ? "Atualize os dados da justificativa em massa."
              : "Aplique uma justificativa para um grupo de colaboradores em uma única operação."
          }}
        </p>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="subtitle-1 font-weight-bold">Dados da justificativa</v-card-title>
      <v-divider />
      <v-card-text class="pt-5">
        <v-form ref="formRef">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-menu
                v-model="menuDataReferencia"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="form.data_referencia ? formatarData(form.data_referencia) : ''"
                    label="Data de referência *"
                    outlined
                    dense
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :rules="[() => !!form.data_referencia || 'Data de referência obrigatória']"
                  />
                </template>
                <v-date-picker
                  v-model="form.data_referencia"
                  locale="pt-BR"
                  :max="hoje"
                  @input="menuDataReferencia = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.motivo"
                :items="motivos"
                label="Motivo *"
                outlined
                dense
                :rules="[(v) => !!v || 'Motivo obrigatório']"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="form.colaboradores_ids"
                :items="colaboradores"
                item-text="nome"
                item-value="id"
                label="Colaboradores *"
                outlined
                dense
                multiple
                chips
                deletable-chips
                small-chips
                :loading="carregandoColaboradores"
                prepend-inner-icon="mdi-account-search"
                :rules="[(v) => (v && v.length > 0) || 'Selecione ao menos um colaborador']"
                :menu-props="{ maxHeight: 300 }"
                no-data-text="Nenhum colaborador encontrado"
              />
              <div class="d-flex justify-end mt-2">
                <v-btn text x-small color="primary" @click="selecionarTodos">
                  Selecionar todos
                </v-btn>
                <v-btn text x-small color="error" @click="limparSelecao"> Limpar seleção </v-btn>
              </div>
              <p v-if="form.colaboradores_ids.length" class="caption mt-1 mb-0">
                {{ form.colaboradores_ids.length }} colaborador{{
                  form.colaboradores_ids.length !== 1 ? "es" : ""
                }}
                selecionado{{ form.colaboradores_ids.length !== 1 ? "s" : "" }}.
              </p>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.observacao"
                label="Observação (opcional)"
                outlined
                dense
                rows="3"
                counter="500"
                :rules="[(v) => !v || v.length <= 500 || 'Máximo 500 caracteres', validarTexto]"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-4 py-3">
        <v-spacer />
        <v-btn text :disabled="enviando" @click="$router.go(-1)">Cancelar</v-btn>
        <v-btn
          color="primary"
          :loading="enviando"
          :disabled="!podeEnviar || enviando"
          @click="enviar"
        >
          <v-icon left small>mdi-account-check</v-icon>
          {{ modoEdicao ? "Salvar Alterações" : "Aplicar Justificativa" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </main-template>
</template>

<script>
import { motivosColaboradores } from "@/plugins/motivos_colaboradores";

export default {
  name: "NovaJustificativaMassaPage",
  data() {
    return {
      enviando: false,
      carregandoColaboradores: false,
      motivos: motivosColaboradores,
      menuDataReferencia: false,
      modoEdicao: false,
      editarId: null,
      colaboradores: [],
      form: {
        data_referencia: "",
        motivo: "",
        colaboradores_ids: [],
        observacao: "",
      },
    };
  },
  computed: {
    hoje() {
      return new Date().toISOString().slice(0, 10);
    },
    podeEnviar() {
      if (!this.form.data_referencia) return false;
      if (!this.form.motivo) return false;
      if (!this.form.colaboradores_ids.length) return false;
      if (this.form.observacao && this.form.observacao.length > 500) return false;
      if (this.form.observacao && this.validarTexto(this.form.observacao) !== true) return false;
      return true;
    },
  },
  async created() {
    this.carregarColaboradores();
    const editarId = this.$route.query.id;
    if (editarId) {
      this.modoEdicao = true;
      this.editarId = editarId;
      await this.carregarParaEdicao(editarId);
    }
  },
  methods: {
    async carregarColaboradores() {
      try {
        this.carregandoColaboradores = true;
        const res = await this.$services.colaboradorPontosService.listarColaboradores();
        this.colaboradores = Array.isArray(res) ? res : res.data || [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoColaboradores = false;
      }
    },
    async carregarParaEdicao(id) {
      try {
        const item = await this.$services.colaboradorPontosService.buscarJustificativaMassa(id);
        this.form = {
          data_referencia: String(item.data_referencia).split("T")[0],
          motivo: item.motivo,
          colaboradores_ids:
            item.colaboradores_ids ||
            (item.colaboradores ? item.colaboradores.map((c) => c.id) : []),
          observacao: item.observacao || "",
        };
      } catch (error) {
        this.$handleError(error);
        this.$router.go(-1);
      }
    },
    formatarData(data) {
      if (!data) return "";
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    },
    validarTexto(v) {
      if (!v) return true;
      if (/\p{Emoji_Presentation}/u.test(v)) return "Emojis não são permitidos";
      if (/[<>{}[\]\\|^~`]/u.test(v)) return "Caracteres especiais não são permitidos";
      return true;
    },
    selecionarTodos() {
      this.form.colaboradores_ids = this.colaboradores.map((c) => c.id);
    },
    limparSelecao() {
      this.form.colaboradores_ids = [];
    },
    async enviar() {
      if (!this.$refs.formRef.validate()) return;

      try {
        this.enviando = true;
        const payload = {
          data_referencia: this.form.data_referencia,
          motivo: this.form.motivo,
          colaboradores_ids: this.form.colaboradores_ids,
          observacao: this.form.observacao || null,
        };
        if (this.modoEdicao) {
          await this.$services.colaboradorPontosService.atualizarJustificativaMassa(
            this.editarId,
            payload
          );
          this.$toast.success("Justificativa em massa atualizada com sucesso!");
        } else {
          await this.$services.colaboradorPontosService.criarJustificativaMassa(payload);
          this.$toast.success("Justificativa em massa aplicada com sucesso!");
        }
        this.$router.push({ name: "colaboradores.justificativaMassa" });
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.enviando = false;
      }
    },
  },
};
</script>
